import { InitResponse, LLMResponse, LoadNewContentResponse, Parameter } from "../types";
import loginService from "./loginService";

type Params = {
    [key: string]: any;
};

export interface CompanyData {
    name: string;
    number: string;
    address: string;
}

export interface SendQuoteParameterResponse {
    llmResponse: LLMResponse;
    valid: boolean;
}

class QuoteService {
    initQuote = async (data: Params): Promise<InitResponse> => {
        const url = "quoter/init";
        const response = await sendRequest(data, url, "POST") as InitResponse;
        return response;
    }

    loadNewContent = async (action: string, data: Params): Promise<LoadNewContentResponse> => {
        data["action"] = action
        const url = "quoter/load-new-content";
        const response = await sendRequest(data, url, "POST");
        return response;
    }

    getQuote = async (data: Params) => {
        const url = "quoter/quote";
        const response = await sendRequest(data, url, "POST");
        return response;
    }

    sendQuoteParameter = async (data: Params): Promise<SendQuoteParameterResponse> => {
        const url = "quoter/quote-parameter";
        const response = await sendRequest(data, url, "POST")
        return response as SendQuoteParameterResponse;
    }

    getFileteredProfessions = async (filter: string, onlyShowBindable: boolean): Promise<string[]> => {
        const url = `/quoter/filtered-professions?filter=${filter}&onlyShowBindable=${onlyShowBindable ? "true" : "false"
            }`;
        try {
            const response = await fetch(url);
            if (!response.ok) {
                return ["No Matches Found"];
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching professions:", error);
            return ["No Matches Found"];
        }
    };

    /**
     * Request a profession. Used when the users' profession is not in the list
     * 
     * Returns whether it was successful or not
     */
    requestProfession = async (profession: string, email: string): Promise<boolean> => {
        const url = `/quoter/request-profession?profession=${profession}&email=${email}`;
        try {
            const response = await fetch(url, { method: "POST" });
            return response.ok;
        } catch (error) {
            console.error("Error requesting profession:", error);
            return false;
        }
    };

    /**
     * @param companyName
     * @returns A "dictionary" of company data, where the key is the company name as key.
     */
    getFilteredCompanies = async (companyName): Promise<{ [key: string]: CompanyData }> => {
        const url = `/quoter/filtered-companies?filter=${companyName}`;
        try {
            const response = await fetch(url);
            if (!response.ok) {
                return {};
            }

            const data = await response.json();

            let mappedCompanyData = {};
            data.forEach(function (el) {
                if (el["company_status"] === "active") {
                    mappedCompanyData[el["title"]] = {
                        name: el["title"],
                        number: el["company_number"],
                        address: el["address"],
                    };
                }
            });
            return mappedCompanyData;
        } catch (error) {
            console.error("Error fetching companies:", error);
            return {};
        }
    };

    sendReferralPreferences = async (referral: boolean) => {
        try {
            const response = await fetch("quoter/referral", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(referral),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return true;
        } catch (error) {
            console.error("Error submitting referral preference:", error);
            return false;
        }
    }

    changeInstallation = async (installation: string) => {
        await fetch("quoter/installation?installation=" + installation, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            }
        });
    }

    getAllPages = async () => {
        const url = "quoter/page";
        const response = await sendRequest(null, url, "GET");
        return response;
    }

    navigateToPage = async (index: number) => {
        const url = "quoter/page?index=" + index.toString();
        const response = await sendRequest(null, url, "POST");
        return response;
    }

    getQuoteSteps = async () => {
        const url = "quoter/quote-steps";
        const response = await sendRequest(null, url, "GET");
        return response.quoteSteps;
    }
}

async function sendRequest(data: Params, url: string, method: string) {
    const response = await fetch(url, {
        method: method,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loginService.getAccessToken()}`,
        },
        body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
        const errorData = await response.json();
        return errorData;
    }

    try {
        const responseData = await response.json();
        return responseData;
    } catch (e) {
        return null;
    }
}

export default new QuoteService();

export class QuoteServiceError extends Error {
    errors: string[];

    constructor(message: string, errors: string[]) {
        super(message);
        this.name = this.constructor.name;
        this.errors = errors;
        Object.setPrototypeOf(this, QuoteServiceError.prototype);
    }
}